<template>
  <div>
    <dl class="grid grid-cols-1 gap-5 sm:grid-cols-3">
      <BaseCard>
        <BaseCardBody>
          <dt class="text-sm font-medium text-gray-500 truncate">Impressions</dt>
          <dd class="mt-1 text-sm font-semibold text-gray-900">
            <apexchart
              type="line"
              height="240"
              :options="getBaseChartOptions()"
              :series="[impressions]"
            />
          </dd>
        </BaseCardBody>
      </BaseCard>
      <BaseCard>
        <BaseCardBody>
          <dt class="text-sm font-medium text-gray-500 truncate">Reach</dt>
          <dd class="mt-1 text-sm font-semibold text-gray-900">
            <apexchart
              type="line"
              height="240"
              :options="getBaseChartOptions()"
              :series="[reach]"
            />
          </dd>
        </BaseCardBody>
      </BaseCard>
      <BaseCard>
        <BaseCardBody>
          <dt class="text-sm font-medium text-gray-500 truncate">Click-Through Rate</dt>
          <dd class="mt-1 text-sm font-semibold text-gray-900">
            <apexchart
              type="line"
              height="240"
              :options="getPercentPerDateChartOptions()"
              :series="[clickThroughRate]"
            />
          </dd>
        </BaseCardBody>
      </BaseCard>
      <template v-if="$isAdminOrSuperAdmin">
        <BaseCard>
          <BaseCardBody>
            <dt class="text-sm font-medium text-gray-500 truncate">Cost</dt>
            <dd class="mt-1 text-sm font-semibold text-gray-900">
              <apexchart
                type="line"
                height="240"
                :options="getEuroPerDateChartOptions()"
                :series="[spend]"
              />
            </dd>
          </BaseCardBody>
        </BaseCard>
        <BaseCard>
          <BaseCardBody>
            <dt class="text-sm font-medium text-gray-500 truncate">Cost Per Mille</dt>
            <dd class="mt-1 text-sm font-semibold text-gray-900">
              <apexchart
                type="line"
                height="240"
                :options="getEuroPerDateChartOptions()"
                :series="[spendPerMille]"
              />
            </dd>
          </BaseCardBody>
        </BaseCard>
        <BaseCard>
          <BaseCardBody>
            <dt class="text-sm font-medium text-gray-500 truncate">Cost Per Click</dt>
            <dd class="mt-1 text-sm font-semibold text-gray-900">
              <apexchart
                type="line"
                height="240"
                :options="getEuroPerDateChartOptions()"
                :series="[spendPerClick]"
              />
            </dd>
          </BaseCardBody>
        </BaseCard>
      </template>
    </dl>
  </div>
</template>

<script>
import moment from 'moment';
import numbro from 'numbro';

export default {
  props: {
    insights: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    labels() {
      return this.insights.map((insight) => insight.time);
    },
    spend() {
      const data = this.insights.map((insight) => {
        let amount = null;
        if (insight.data) {
          amount = insight.data.spend
            ? Number(Math.round(insight.data.spend / 100 + 'e2') + 'e-2')
            : 0;
        }
        return amount;
      });

      return {
        name: 'Cost',
        data,
      };
    },
    impressions() {
      const data = this.insights.map((insight) => {
        return insight.data ? insight.data.impressions : null;
      });

      return {
        name: 'Impressions',
        data,
      };
    },
    reach() {
      const data = this.insights.map((insight) => {
        return insight.data ? insight.data.reach : null;
      });

      return {
        name: 'Reach',
        data,
      };
    },
    inlineLinkClicks() {
      const data = this.insights.map((insight) => {
        return insight.data ? insight.data.inline_link_clicks : null;
      });

      return {
        name: 'Clicks',
        data,
      };
    },
    clickThroughRate() {
      const data = this.insights.map((insight) => {
        let amount = null;
        if (insight.data) {
          amount = insight.data.impressions
            ? Number(
                Math.round(insight.data.inline_link_clicks / insight.data.impressions + 'e2') +
                  'e-2'
              )
            : 0;
        }
        return amount;
      });

      return {
        name: 'Click-Through Rate',
        data,
      };
    },
    spendPerMille() {
      const data = this.insights.map((insight) => {
        let amount = null;
        if (insight.data) {
          amount = insight.data.inline_link_clicks
            ? Number(
                Math.round(1000 * (insight.data.spend / 100 / insight.data.impressions) + 'e2') +
                  'e-2'
              )
            : 0;
        }

        return amount;
      });

      return {
        name: 'Cost Per Mille',
        data,
      };
    },
    spendPerClick() {
      const data = this.insights.map((insight) => {
        let amount = null;
        if (insight.data) {
          amount = insight.data.inline_link_clicks
            ? Number(
                Math.round(insight.data.spend / 100 / insight.data.inline_link_clicks + 'e2') +
                  'e-2'
              )
            : 0;
        }

        return amount;
      });

      return {
        name: 'Cost Per Click',
        data,
      };
    },
  },
  methods: {
    getBaseChartOptions() {
      return {
        chart: {
          height: 240,
          type: 'line',
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
          animations: {
            enabled: false,
          },
        },
        stroke: {
          width: [5],
          curve: 'smooth',
        },
        legend: {
          show: false,
        },
        labels: [...this.labels],
        title: {
          show: false,
        },
        xaxis: {
          type: 'datetime',
          tickAmount: 4,
          min: this.labels[0],
          max: this.labels[this.labels.index - 1],
          labels: {
            formatter: (val, timestamp) => {
              return moment(new Date(timestamp)).format('DD MMM YY');
            },
          },
        },
        yaxis: {
          labels: {
            formatter: (val) => {
              if (val === undefined || val === null) {
                return 'N/A';
              }
              return numbro(val).format({
                thousandSeparated: true,
              });
            },
          },
        },
      };
    },
    getPercentPerDateChartOptions() {
      return {
        ...this.getBaseChartOptions(),
        yaxis: {
          labels: {
            formatter: (val) => {
              if (val === undefined || val === null) {
                return 'N/A';
              }
              return numbro(val).format({
                output: 'percent',
                mantissa: 1,
              });
            },
          },
        },
      };
    },
    getEuroPerDateChartOptions() {
      return {
        ...this.getBaseChartOptions(),
        yaxis: {
          labels: {
            formatter: (val) => {
              if (val === undefined || val === null) {
                return 'N/A';
              }

              const amount = Number(Math.round(val + 'e2') + 'e-2').toFixed(2);

              return `${amount} \u20AC`;
            },
          },
        },
      };
    },
  },
};
</script>
